import { useEffect, useMemo, useRef } from "react";
import { Box, Grid, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as echarts from "echarts/core";
import { GaugeChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";
import { geminiChartPalette } from "../../../../assets/theme/geminiPalette";
import type { SourceSystemListRow } from "../hooks/useDashboardOverviewPanels";
import type { SourceSystemWindowCounts } from "../../../../services/dashboardOpenSearchService";

echarts.use([GaugeChart, TooltipComponent, CanvasRenderer]);

const GAUGE_SIZE = 132;

type WindowKey = "last30Min" | "last1Hour" | "today";

type WindowSpec = {
  key: WindowKey;
  prevKey: keyof Pick<
    SourceSystemWindowCounts,
    "prevWeek30Min" | "prevWeek1Hour" | "prevWeekToday"
  >;
  labelKey: "dashboard.window30min" | "dashboard.window1h" | "dashboard.today";
};

const WINDOWS: WindowSpec[] = [
  { key: "today", prevKey: "prevWeekToday", labelKey: "dashboard.today" },
  {
    key: "last1Hour",
    prevKey: "prevWeek1Hour",
    labelKey: "dashboard.window1h",
  },
  {
    key: "last30Min",
    prevKey: "prevWeek30Min",
    labelKey: "dashboard.window30min",
  },
];

export function activityShare(current: number, previous: number): number {
  const peak = Math.max(current, previous);
  if (peak <= 0) return 0;
  return Math.round((current / peak) * 100);
}

export function increaseScore(counts: SourceSystemWindowCounts): number {
  return (
    counts.last30Min -
    counts.prevWeek30Min +
    (counts.last1Hour - counts.prevWeek1Hour) +
    (counts.today - counts.prevWeekToday)
  );
}

function windowTone(
  current: number,
  previous: number,
  colors: { success: string; warning: string; error: string; muted: string },
): string {
  if (current <= 0 && previous <= 0) return colors.muted;
  if (current > previous) {
    if (previous <= 0 || current >= previous * 2) return colors.error;
    return colors.warning;
  }
  return colors.success;
}

type RingHostProps = {
  option: EChartsCoreOption;
};

function RingGaugeHost({ option }: RingHostProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const chart = echarts.init(host);
    chartRef.current = chart;
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(host);

    return () => {
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, true);
  }, [option]);

  return <Box ref={hostRef} sx={{ width: GAUGE_SIZE, height: GAUGE_SIZE }} />;
}

function SourceSystemRingCard({ row }: { row: SourceSystemListRow }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const chartColors = geminiChartPalette(
    theme.palette.mode === "dark" ? "dark" : "light",
  );
  const counts = row.counts;

  const toneColors = useMemo(
    () => ({
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      muted: theme.palette.text.disabled,
    }),
    [
      theme.palette.error.main,
      theme.palette.success.main,
      theme.palette.text.disabled,
      theme.palette.warning.main,
    ],
  );

  const windows = useMemo(
    () =>
      WINDOWS.map((spec) => {
        const current = counts[spec.key];
        const previous = counts[spec.prevKey];
        return {
          ...spec,
          label: t(spec.labelKey),
          current,
          previous,
          share: activityShare(current, previous),
          color: windowTone(current, previous, toneColors),
        };
      }),
    [counts, t, toneColors],
  );

  const option = useMemo<EChartsCoreOption>(
    () => ({
      animationDuration: 360,
      tooltip: {
        trigger: "item",
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary, fontSize: 12 },
        formatter: (raw: unknown) => {
          const item = raw as {
            name?: string;
            data?: { current?: number; previous?: number };
          };
          const current = item.data?.current ?? 0;
          const previous = item.data?.previous ?? 0;
          return `${item.name ?? ""}<br/>${t("dashboard.gaugeVsPrev", { current, previous })}`;
        },
      },
      series: [
        {
          type: "gauge",
          startAngle: 90,
          endAngle: -270,
          min: 0,
          max: 100,
          radius: "96%",
          center: ["50%", "50%"],
          pointer: { show: false },
          anchor: { show: false },
          progress: {
            show: true,
            overlap: false,
            roundCap: true,
            clip: false,
          },
          axisLine: {
            lineStyle: {
              width: 18,
              color: [
                [
                  1,
                  theme.palette.mode === "dark"
                    ? chartColors.splitLine
                    : theme.palette.grey[200],
                ],
              ],
            },
          },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          data: windows.map((window) => ({
            value: window.share,
            name: window.label,
            current: window.current,
            previous: window.previous,
            itemStyle: { color: window.color },
            title: { show: false },
            detail: { show: false },
          })),
          title: { show: false },
          detail: { show: false },
        },
      ],
    }),
    [
      chartColors.splitLine,
      t,
      theme.palette.background.paper,
      theme.palette.divider,
      theme.palette.grey,
      theme.palette.mode,
      theme.palette.text.primary,
      windows,
    ],
  );

  const sourceSystemName = row.sourceSystemName.trim() || row.sourceSystemId;
  const title =
    row.definition.trim() || row.value.trim() || sourceSystemName || "—";
  const meta = [sourceSystemName, row.type.trim(), row.value.trim()]
    .filter(Boolean)
    .filter((part) => part !== title)
    .join(" · ");

  return (
    <Paper
      elevation={0}
      sx={{
        height: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1.25,
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            position: "relative",
            width: GAUGE_SIZE,
            height: GAUGE_SIZE,
            flexShrink: 0,
          }}
        >
          <RingGaugeHost option={option} />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
            >
              {counts.today}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              {t("dashboard.today")}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={700} noWrap title={title}>
              {title}
            </Typography>
            {meta ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={meta}
                sx={{ display: "block" }}
              >
                {meta}
              </Typography>
            ) : null}
          </Box>

          <Stack spacing={0.5}>
            {windows.map((window) => (
              <Stack
                key={window.key}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: window.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 52, flexShrink: 0, fontWeight: 700 }}
                >
                  {window.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {window.current}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {t("dashboard.prev")} {window.previous}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

function sortRowsByIncrease(rows: SourceSystemListRow[]) {
  return [...rows].sort(
    (left, right) => increaseScore(right.counts) - increaseScore(left.counts),
  );
}

type DashboardSourceSystemGaugesProps = {
  rows: SourceSystemListRow[];
};

export default function DashboardSourceSystemGauges({
  rows,
}: DashboardSourceSystemGaugesProps) {
  const { t } = useTranslation();
  const rankedRows = useMemo(() => sortRowsByIncrease(rows), [rows]);

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: "0.08em",
          color: "text.secondary",
          display: "block",
          mb: 0.75,
          fontWeight: 600,
        }}
      >
        {t("dashboard.sourceSystemList")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.25, mt: -0.25, lineHeight: 1.35 }}
      >
        {t("dashboard.sourceSystemListHint")}
      </Typography>

      {rankedRows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.sourceSystemEmpty")}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {rankedRows.map((row) => (
            <Grid key={row.rowKey} size={{ xs: 12, md: 6 }}>
              <SourceSystemRingCard row={row} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
